/** Page chrome: the title row, loading and error states, empty states and the pager. */
import { AlertTriangleIcon, InboxIcon } from "lucide-react";
import type * as React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { errorMessage } from "@/api/queries";

export function PageHeader({
  title,
  subtitle,
  actions,
  className,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <header className={cn("flex flex-wrap items-end justify-between gap-x-6 gap-y-3", className)}>
      <div className="flex min-w-0 flex-col gap-1">
        <h1 className="font-display text-2xl leading-tight text-heading">{title}</h1>
        {subtitle ? <div className="text-base text-muted">{subtitle}</div> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

export function SectionTitle({ children, count, id }: { children: React.ReactNode; count?: number; id?: string }) {
  return (
    <h2 id={id} className="m-0 flex items-center gap-2 font-display text-lg leading-snug text-heading">
      {children}
      {count !== undefined ? (
        <span className="rounded-pill bg-surface-soft px-2 py-0.5 font-mono text-xs font-medium text-heading" data-testid="count">
          {count.toLocaleString("en-US")}
        </span>
      ) : null}
    </h2>
  );
}

export function Loading({ label = "Loading" }: { label?: string }) {
  return (
    <div role="status" aria-live="polite" className="flex flex-col gap-3 py-6">
      <span className="sr-only">{label}</span>
      {[0, 1, 2].map((row) => (
        <div key={row} className="h-10 animate-pulse rounded-sm bg-surface-sunken" />
      ))}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex flex-wrap items-center gap-3 rounded-md border border-danger bg-danger-tint px-4 py-3 text-sm text-danger-ink">
      <AlertTriangleIcon aria-hidden className="size-4" />
      <span>{errorMessage(error)}</span>
      {onRetry ? (
        <Button size="sm" variant="outline" className="ms-auto" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

export function Empty({ title, children, id }: { title: string; children?: React.ReactNode; id?: string }) {
  return (
    <div id={id} className="flex flex-col items-center gap-3 px-5 py-10 text-center">
      <span className="inline-flex size-12 items-center justify-center rounded-pill bg-surface-soft text-muted">
        <InboxIcon aria-hidden className="size-6" />
      </span>
      <p className="m-0 font-display text-lg leading-snug text-heading">{title}</p>
      {children ? <div className="flex flex-wrap justify-center gap-3 text-sm text-muted">{children}</div> : null}
    </div>
  );
}

export function Pager({
  page,
  totalPages,
  onPage,
  summary,
}: {
  page: number;
  totalPages: number;
  onPage: (page: number) => void;
  summary: React.ReactNode;
}) {
  return (
    <nav aria-label="Pages" className="flex flex-wrap items-center justify-between gap-3 max-sm:justify-center">
      <span className="text-sm text-muted">{summary}</span>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Previous
        </Button>
        <span className="font-mono text-xs text-muted" aria-current="page">
          Page {page} of {totalPages}
        </span>
        <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>
          Next
        </Button>
      </div>
    </nav>
  );
}
